import { useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText,
  Link2,
  PlayCircle,
  Send,
} from "lucide-react";
import { useParams } from "react-router-dom";

import {
  SelfCourse,
  SelfCourseEnrollment,
  SelfCourseLesson,
  selfCoursesApi,
} from "../api/selfCourses";
import LinkedText from "../components/NewView/Common/LinkedText";
import ProgramMaterials from "../components/NewView/Common/ProgramMaterials";

const lessonLabels: Record<string, string> = {
  dialog: "Матеріал",
  video: "Відео",
  material: "Матеріал для самостійного вивчення",
  lab: "Лабораторна",
  quiz: "Тренувальне завдання",
  assignment: "Оцінюване завдання",
};

const getEmbeddableVideoUrl = (url?: string) => {
  if (!url) return "";

  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.replace(/^www\./, "");

    if (hostname === "youtu.be") {
      const videoId = parsed.pathname.split("/").filter(Boolean)[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }

    if (hostname === "youtube.com" || hostname === "m.youtube.com") {
      if (parsed.pathname.startsWith("/embed/")) return url;

      const watchId = parsed.searchParams.get("v");
      if (watchId) return `https://www.youtube.com/embed/${watchId}`;

      const parts = parsed.pathname.split("/").filter(Boolean);
      const shortsIndex = parts.indexOf("shorts");
      if (shortsIndex >= 0 && parts[shortsIndex + 1]) {
        return `https://www.youtube.com/embed/${parts[shortsIndex + 1]}`;
      }
    }

    if (hostname === "drive.google.com") {
      const fileMatch = parsed.pathname.match(/\/file\/d\/([^/]+)/);
      if (fileMatch?.[1]) {
        return `https://drive.google.com/file/d/${fileMatch[1]}/preview`;
      }
    }

    if (hostname === "vimeo.com") {
      const videoId = parsed.pathname.split("/").filter(Boolean)[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : url;
    }

    return url;
  } catch {
    return url;
  }
};

const findFirstLesson = (course?: SelfCourse | null) =>
  course?.modules?.flatMap((module) => module.lessons)?.[0] || null;

const SelfCourseLearnPage = () => {
  const { id = "" } = useParams();
  const [course, setCourse] = useState<SelfCourse | null>(null);
  const [enrollment, setEnrollment] = useState<SelfCourseEnrollment | null>(null);
  const [activeLessonId, setActiveLessonId] = useState("");
  const [openModules, setOpenModules] = useState<Record<string, boolean>>({});
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [submission, setSubmission] = useState({ link: "", comment: "" });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const loadCourse = async () => {
    const data = await selfCoursesApi.getMine(id);
    setCourse(data.course);
    setEnrollment(data.enrollment);
    const firstLesson = findFirstLesson(data.course);
    setActiveLessonId((current) => current || firstLesson?._id || "");
    setOpenModules(
      Object.fromEntries(data.course.modules.map((module, index) => [module._id, index === 0]))
    );
  };

  useEffect(() => {
    setLoading(true);
    loadCourse()
      .catch(() => setMessage("Не вдалося завантажити курс."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const activeLesson = useMemo<SelfCourseLesson | null>(() => {
    if (!course) return null;
    return (
      course.modules
        .flatMap((module) => module.lessons)
        .find((lesson) => lesson._id === activeLessonId) || null
    );
  }, [course, activeLessonId]);

  const progressByLesson = useMemo(
    () =>
      new Map(
        (enrollment?.lessons || []).map((lesson) => [
          String(lesson.lessonId),
          lesson,
        ])
      ),
    [enrollment]
  );

  const lastSubmission = useMemo(() => {
    if (!activeLesson) return null;
    return [...(enrollment?.submissions || [])]
      .reverse()
      .find((item) => String(item.lessonId) === activeLesson._id);
  }, [activeLesson, enrollment?.submissions]);

  const markComplete = async () => {
    if (!course || !activeLesson) return;
    setMessage("");
    const next = await selfCoursesApi.completeLesson(course._id, activeLesson._id);
    setEnrollment(next);
    setMessage("Урок позначено як пройдений.");
  };

  const submitWork = async () => {
    if (!course || !activeLesson) return;
    if (!submission.link.trim()) {
      setMessage("Додайте посилання на виконану роботу.");
      return;
    }
    const next = await selfCoursesApi.submitLesson(course._id, activeLesson._id, {
      link: submission.link.trim(),
      comment: submission.comment.trim(),
    });
    setEnrollment(next);
    setSubmission({ link: "", comment: "" });
    setMessage("Роботу надіслано на перевірку.");
  };

  const submitQuiz = async () => {
    if (!course || !activeLesson) return;
    const result = await selfCoursesApi.submitQuiz(course._id, activeLesson._id, answers);
    setEnrollment(result.enrollment);
    setMessage(
      result.passed
        ? `Тест пройдено. Результат: ${result.score}%.`
        : `Результат: ${result.score}%. Спробуй ще раз.`
    );
  };

  if (loading) {
    return <section className="p-6 text-sm font-bold">Завантажуємо курс...</section>;
  }

  if (!course) {
    return <section className="p-6 text-sm font-bold">Курс не знайдено.</section>;
  }

  if (!activeLesson) {
    return (
      <section className="bg-[#eef3fb] p-3 sm:p-5 xl:h-[calc(100dvh-90px)] xl:overflow-hidden">
        <div className="grid gap-4 xl:h-full xl:min-h-0 xl:grid-cols-[390px_minmax(0,1fr)]">
          <aside className="min-h-0 overflow-hidden rounded-[22px] border border-[#D8E0EC] bg-white xl:flex xl:h-full xl:flex-col">
            <div className="shrink-0 border-b border-[#D8E0EC] p-5">
              <h1 className="text-xl font-black text-black">{course.title}</h1>
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-[#E8EDF5]">
                <div
                  className="h-full bg-[#118221]"
                  style={{ width: `${enrollment?.progress || 0}%` }}
                />
              </div>
              <p className="mt-2 text-xs font-bold text-[#66708A]">
                Прогрес: {enrollment?.progress || 0}%
              </p>
            </div>

            <div className="max-h-[60vh] overflow-y-auto xl:max-h-none xl:min-h-0 xl:flex-1">
              {course.modules.length === 0 ? (
                <div className="p-5 text-sm font-bold text-[#66708A]">
                  Модулі ще не додані.
                </div>
              ) : (
                course.modules.map((module) => {
                  const open = openModules[module._id];
                  return (
                    <div key={module._id} className="border-b border-[#D8E0EC]">
                      <button
                        type="button"
                        onClick={() =>
                          setOpenModules((prev) => ({
                            ...prev,
                            [module._id]: !prev[module._id],
                          }))
                        }
                        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                      >
                        <span>
                          <span className="block text-xs font-black text-[#6B7BA0]">
                            {module.subtitle || `Модуль ${module.order}`}
                          </span>
                          <span className="mt-1 block text-sm font-black text-[#1F2430]">
                            {module.title}
                          </span>
                        </span>
                        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>

                      {open && (
                        <div className="px-5 pb-5 text-sm font-bold text-[#66708A]">
                          У цьому модулі ще немає уроків.
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </aside>

          <main className="grid min-h-[420px] place-items-center rounded-[24px] border border-[#D8E0EC] bg-white p-6 text-center xl:h-full">
            <div className="max-w-lg">
              <p className="text-sm font-black uppercase tracking-[0.14em] text-[#8a6f00]">
                Курс створено
              </p>
              <h2 className="mt-2 text-3xl font-black uppercase leading-none tracking-[-0.04em] text-black">
                Уроки ще не додані
              </h2>
              <p className="mt-4 text-sm font-bold leading-7 text-[#66708A]">
                Адміністратор уже створив курс і модуль. Коли в адмінці
                додадуть перший урок, матеріал або тест, він з’явиться тут
                автоматично.
              </p>
            </div>
          </main>
        </div>
      </section>
    );
  }

  const activeVideoUrl = getEmbeddableVideoUrl(activeLesson.videoUrl);

  return (
    <section className="bg-[#eef3fb] p-3 sm:p-5 xl:h-[calc(100dvh-90px)] xl:overflow-hidden">
      <div className="grid gap-4 xl:h-full xl:min-h-0 xl:grid-cols-[390px_minmax(0,1fr)]">
        <aside className="min-h-0 overflow-hidden rounded-[22px] border border-[#D8E0EC] bg-white xl:flex xl:h-full xl:flex-col">
          <div className="shrink-0 border-b border-[#D8E0EC] p-5">
            <h1 className="text-xl font-black text-black">{course.title}</h1>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-[#E8EDF5]">
              <div
                className="h-full bg-[#118221]"
                style={{ width: `${enrollment?.progress || 0}%` }}
              />
            </div>
            <p className="mt-2 text-xs font-bold text-[#66708A]">
              Прогрес: {enrollment?.progress || 0}%
            </p>
          </div>

          <div className="max-h-[60vh] overflow-y-auto xl:max-h-none xl:min-h-0 xl:flex-1">
            {course.modules.map((module) => {
              const open = openModules[module._id];
              return (
                <div key={module._id} className="border-b border-[#D8E0EC]">
                  <button
                    type="button"
                    onClick={() =>
                      setOpenModules((prev) => ({
                        ...prev,
                        [module._id]: !prev[module._id],
                      }))
                    }
                    className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                  >
                    <span>
                      <span className="block text-xs font-black text-[#6B7BA0]">
                        {module.subtitle || `Модуль ${module.order}`}
                      </span>
                      <span className="mt-1 block text-sm font-black text-[#1F2430]">
                        {module.title}
                      </span>
                    </span>
                    {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>

                  {open && (
                    <div className="space-y-1 px-4 pb-4">
                      {module.lessons.map((lesson) => {
                        const done = progressByLesson.get(lesson._id);
                        const active = lesson._id === activeLesson._id;
                        return (
                          <button
                            key={lesson._id}
                            type="button"
                            onClick={() => {
                              setActiveLessonId(lesson._id);
                              setMessage("");
                            }}
                            className={`grid w-full grid-cols-[26px_minmax(0,1fr)] gap-3 rounded-xl px-2 py-2 text-left transition ${
                              active ? "bg-[#FFF3A8]" : "hover:bg-[#F4F7FB]"
                            }`}
                          >
                            <span
                              className={`mt-1 grid h-5 w-5 place-items-center rounded-full ${
                                done?.status === "completed" || done?.status === "submitted"
                                  ? "bg-[#118221] text-white"
                                  : "bg-[#E8EDF5]"
                              }`}
                            >
                              {(done?.status === "completed" || done?.status === "submitted") && (
                                <Check size={13} />
                              )}
                            </span>
                            <span>
                              <span className="block text-sm font-semibold leading-5 text-[#20242C]">
                                {lesson.title}
                              </span>
                              <span className="mt-0.5 block text-xs font-semibold text-[#6B7BA0]">
                                {lessonLabels[lesson.type]} • {lesson.durationMinutes} хв
                                {done?.score !== null && done?.score !== undefined
                                  ? ` • Оцінка: ${done.score}%`
                                  : ""}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        <main className="min-h-0 rounded-[24px] border border-[#D8E0EC] bg-white p-5 sm:p-7 xl:h-full xl:overflow-y-auto">
          {message && (
            <div className="mb-5 rounded-2xl border border-[#E5D2C5] bg-[#FFF8F3] px-4 py-3 text-sm font-bold text-[#8A4A2E]">
              {message}
            </div>
          )}

          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#E8EDF5] pb-5">
            <div>
              <span className="inline-flex rounded-full bg-[#EEF3FB] px-3 py-1 text-xs font-black text-[#586A94]">
                {lessonLabels[activeLesson.type]} • {activeLesson.durationMinutes} хв
              </span>
              <h2 className="mt-4 text-3xl font-black uppercase leading-none tracking-[-0.05em] text-black sm:text-5xl">
                {activeLesson.title}
              </h2>
            </div>
            <button
              type="button"
              onClick={markComplete}
              className="inline-flex h-11 items-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[4px_4px_0_#000]"
            >
              <Check size={17} /> Позначити пройденим
            </button>
          </div>

          <div className="mt-6 space-y-6">
            {activeLesson.videoUrl ? (
              <div className="rounded-3xl border-2 border-black bg-black p-2">
                <div className="aspect-video overflow-hidden rounded-2xl bg-[#111]">
                  <iframe
                    title={activeLesson.title}
                    src={activeVideoUrl}
                    className="h-full w-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
                <a
                  href={activeLesson.videoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-[#ffd400] px-4 py-2 text-sm font-black text-black"
                >
                  <ExternalLink size={16} /> Відкрити відео окремо
                </a>
              </div>
            ) : activeLesson.type === "video" ? (
              <div className="grid min-h-[260px] place-items-center rounded-3xl border-2 border-black bg-[#111] text-white">
                <div className="text-center">
                  <PlayCircle className="mx-auto text-[#ffd400]" size={54} />
                  <p className="mt-3 text-sm font-bold text-white/75">
                    Тут буде відео після додавання посилання адміністратором.
                  </p>
                </div>
              </div>
            ) : null}

            {activeLesson.content && (
              <div className="rounded-3xl border border-[#E8E1D8] bg-[#fffdf7] p-5 text-base font-semibold leading-8 text-[#3F3A34]">
                <LinkedText text={activeLesson.content} />
              </div>
            )}

            {(activeLesson.materials?.length || 0) > 0 && (
              <ProgramMaterials
                materials={activeLesson.materials || []}
                title="Матеріали уроку"
                inlineSinglePreview
              />
            )}

            {activeLesson.type === "quiz" && (
              <div className="rounded-3xl border-2 border-black bg-white p-5">
                <h3 className="text-xl font-black text-black">Тест</h3>
                <div className="mt-4 space-y-4">
                  {(activeLesson.questions || []).map((question) => (
                    <div
                      key={question._id || question.question}
                      className="rounded-2xl bg-[#F7F4EF] p-4"
                    >
                      <p className="font-black text-black">{question.question}</p>
                      {question.allowMultiple && (
                        <p className="mt-1 text-xs font-bold text-[#6B7BA0]">
                          Оберіть усі правильні варіанти.
                        </p>
                      )}
                      <div className="mt-3 space-y-2">
                        {question.options.map((option) => {
                          const questionId = question._id || question.question;
                          const optionId = option._id || option.text;
                          const currentAnswer = answers[questionId];
                          const selectedOptions = Array.isArray(currentAnswer)
                            ? currentAnswer
                            : [];

                          return (
                            <label
                              key={optionId}
                              className="flex items-center gap-2 text-sm font-bold"
                            >
                              <input
                                type={question.allowMultiple ? "checkbox" : "radio"}
                                name={questionId}
                                value={optionId}
                                checked={
                                  question.allowMultiple
                                    ? selectedOptions.includes(optionId)
                                    : currentAnswer === optionId
                                }
                                onChange={() =>
                                  setAnswers((prev) => {
                                    if (!question.allowMultiple) {
                                      return {
                                        ...prev,
                                        [questionId]: optionId,
                                      };
                                    }

                                    const previous = Array.isArray(prev[questionId])
                                      ? (prev[questionId] as string[])
                                      : [];
                                    const next = previous.includes(optionId)
                                      ? previous.filter((id) => id !== optionId)
                                      : [...previous, optionId];

                                    return {
                                      ...prev,
                                      [questionId]: next,
                                    };
                                  })
                                }
                              />
                              {option.text}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={submitQuiz}
                  className="mt-5 inline-flex h-11 items-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[4px_4px_0_#000]"
                >
                  <Send size={17} /> Надіслати тест
                </button>
              </div>
            )}

            {(activeLesson.type === "assignment" || activeLesson.type === "lab") && (
              <div className="rounded-3xl border-2 border-black bg-[#fffdf2] p-5">
                <h3 className="text-xl font-black text-black">
                  {activeLesson.type === "lab"
                    ? "Здати лабораторну"
                    : "Здати практичну роботу"}
                </h3>
                {lastSubmission && (
                  <div className="mt-4 rounded-2xl border border-[#E8E1D8] bg-white p-4 text-sm font-bold">
                    <p>Остання робота: {lastSubmission.status}</p>
                    <a
                      href={lastSubmission.link}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-[#155EEF]"
                    >
                      <Link2 size={14} /> Відкрити посилання <ExternalLink size={13} />
                    </a>
                    {lastSubmission.score !== null && lastSubmission.score !== undefined && (
                      <p className="mt-2">Оцінка: {lastSubmission.score}</p>
                    )}
                    {lastSubmission.adminComment && (
                      <p className="mt-2 text-[#817A72]">
                        Коментар: {lastSubmission.adminComment}
                      </p>
                    )}
                  </div>
                )}
                <div className="mt-4 grid gap-3">
                  <label className="block">
                    <span className="mb-2 block text-sm font-black text-black">
                      {activeLesson.type === "lab"
                        ? "Посилання на перший дизайн"
                        : "Посилання на роботу"}
                    </span>
                    <input
                      value={submission.link}
                      onChange={(event) =>
                        setSubmission((prev) => ({
                          ...prev,
                          link: event.target.value,
                        }))
                      }
                      className="h-12 w-full rounded-2xl border-2 border-black/10 px-4 text-sm font-bold outline-none focus:border-black"
                      placeholder="Встав посилання на Canva-проект"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-black text-black">
                      Коментар
                    </span>
                    <textarea
                      value={submission.comment}
                      onChange={(event) =>
                        setSubmission((prev) => ({
                          ...prev,
                          comment: event.target.value,
                        }))
                      }
                      className="min-h-[120px] w-full rounded-2xl border-2 border-black/10 p-4 text-sm font-bold outline-none focus:border-black"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={submitWork}
                    className="inline-flex h-12 w-fit items-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-6 text-sm font-black text-black shadow-[4px_4px_0_#000]"
                  >
                    <FileText size={17} />{" "}
                    {activeLesson.type === "lab"
                      ? "Надіслати лабораторну"
                      : "Надіслати роботу"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </section>
  );
};

export default SelfCourseLearnPage;
